import { useEffect, useRef, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { getAccountCycle, putAccountCycle } from "@/lib/account-api";
import { useAccount, OWNER_KEY } from "@/lib/account";
import { loadCycle, saveCycle, clearCycle, hasCycleData, mergeCycles, CYCLE_EVENT } from "@/lib/cycle";

const clampInt = (v, lo, hi, d) => Math.min(hi, Math.max(lo, Math.round(Number(v)) || d));
const payload = (c) => ({ starts: c.starts.slice(-12), cycleLength: clampInt(c.cycleLength, 15, 60, 28), periodLength: clampInt(c.periodLength, 1, 15, 5),
  logs: c.logs, pregnant: Boolean(c.pregnant), lmp: c.lmp || null });

// Keeps the browser calendar and the signed-in account in step; asks before merging browser data into an account
export const CycleSync = () => {
  const { user, setMergeOpen } = useAccount();
  const uid = user?.user_id;
  const [linked, setLinked] = useState(false);
  const [pending, setPending] = useState(null);
  const [checking, setChecking] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setMergeOpen(Boolean(pending) || checking); }, [pending, checking, setMergeOpen]);

  useEffect(() => {
    setLinked(false);
    setPending(null);
    if (!uid) return undefined;
    let live = true;
    setChecking(true);
    getAccountCycle().then((res) => {
      if (!live) return;
      const remote = res?.data ?? null;
      const local = loadCycle();
      const link = () => { window.localStorage.setItem(OWNER_KEY, uid); setLinked(true); };
      if (window.localStorage.getItem(OWNER_KEY) === uid || !hasCycleData(local)) {
        if (remote) saveCycle(remote);
        else if (hasCycleData(local)) putAccountCycle(payload(local)).catch(() => {});
        link();
      } else {
        setPending({ remote, link });
      }
    }).catch(() => {}).finally(() => { if (live) setChecking(false); });
    return () => { live = false; };
  }, [uid]);

  useEffect(() => {
    if (!linked) return undefined;
    const push = () => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => putAccountCycle(payload(loadCycle())).catch(() => {}), 800);
    };
    window.addEventListener(CYCLE_EVENT, push);
    return () => { window.removeEventListener(CYCLE_EVENT, push); clearTimeout(timer.current); };
  }, [linked]);

  const choose = (merge) => {
    const { remote, link } = pending;
    const local = loadCycle();
    const next = merge ? (remote ? mergeCycles(remote, local) : local) : remote;
    if (next) saveCycle(next); else clearCycle();
    if (merge) putAccountCycle(payload(loadCycle())).catch(() => {});
    setPending(null);
    link();
  };

  return (
    <AlertDialog open={Boolean(pending)}>
      <AlertDialogContent className="rounded-3xl border-[#C86D51]/20 bg-[#FDFBF7]" data-testid="cycle-merge-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-2xl font-normal text-[#2C221E]">Add this browser's calendar to your account?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#5C4A42]">
            You have period dates and day logs saved on this device. Add them to your account so they follow you everywhere, or keep only what's already in your account.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => choose(false)} data-testid="cycle-merge-skip-button" className="rounded-full">{pending?.remote ? "Use my account only" : "Start fresh"}</AlertDialogCancel>
          <AlertDialogAction onClick={() => choose(true)} data-testid="cycle-merge-confirm-button" className="rounded-full bg-[#2C221E] hover:bg-[#9E4429]">Add to my account</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
